import { useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'

import { cn } from '@/lib/utils'

export interface LoadingBarProps {
  /** true mientras la operación está en curso — la barra avanza sin llegar al 100%. */
  active: boolean
  className?: string
}

/**
 * Barra de progreso "falsa" asintótica: avanza rápido al inicio, desacelera sin
 * llegar nunca al 100% mientras `active` es true, y al pasar a false completa
 * a 100% y se desvanece. Mismo mecanismo que usa NavigationProgress para
 * transiciones de ruta — aquí reutilizable para cualquier operación async
 * (refetch de listas al cambiar filtros, etc.).
 */
export function LoadingBar({ active, className }: LoadingBarProps): JSX.Element {
  const [progress, setProgress] = useState(0)
  const [opacity, setOpacity] = useState(0)
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (active) {
      setOpacity(1)
      setProgress(8)

      tickRef.current = setInterval(() => {
        setProgress((p) => (p >= 82 ? p : p + (82 - p) * 0.12))
      }, 160)

      return () => {
        if (tickRef.current !== null) clearInterval(tickRef.current)
      }
    }

    if (tickRef.current !== null) clearInterval(tickRef.current)

    setProgress(100)
    const t1 = setTimeout(() => setOpacity(0), 200)
    const t2 = setTimeout(() => setProgress(0), 600)

    return () => {
      clearTimeout(t1)
      clearTimeout(t2)
    }
  }, [active])

  return (
    <div
      aria-hidden="true"
      className={cn(
        'pointer-events-none bg-primary transition-[width,opacity] duration-200 ease-out dark:bg-foreground',
        className,
      )}
      style={{ width: `${progress}%`, opacity }}
    />
  )
}
